// Месяц человека: где в его других ведомостях этого месяца стоит то, чего в этой нет.
//
// С 19.10.2026 всё, что тз называет «в месяц», — форма по графику, аванс, официальная часть, —
// берётся один раз на месяц человека в юрлице и лежит в ОДНОЙ из его ведомостей. В остальных по
// этой колонке ноль, и ноль без объяснения читается двумя неправдами сразу: здесь «форму не
// удержали», а в соседней ведомости — «удержали дважды».
//
// ## Откуда названия и суммы соседей
//
// Только из `month_siblings` сетки: сервер кладёт туда ведомости, ВИДИМЫЕ смотрящему, и прячет
// официальную часть у ролей со «скрыто». Шаги объяснения расчёта этого не скажут и не должны:
// они хранятся в строке человека и уходят каждому, кто видит эту ведомость, поэтому сервер пишет
// в них «в другой ведомости этого месяца» без названия. Соседа, которого смотрящему не видно,
// здесь нет вовсе — и подсказка о нём молчит, а не придумывает.

import type {
  MonthSiblingOut,
  PayrollColumnOut,
  PayrollEmployeeOut,
} from '@/shared/api/endpoints/payroll';
import {
  COLUMN_ADVANCE,
  COLUMN_OFFICIAL_PAID,
  COLUMN_OFFICIAL_REST,
} from '@/shared/api/endpoints/payroll';
import { formatRubles } from '@/shared/money/format';

import { авансВКолонке } from './аванс';

/** Ведомость аппарата управления: у неё нет объекта, и названия тоже. */
export const АППАРАТ = 'Аппарат управления';

/** Где в месяце стоит сумма колонки: какая ведомость и сколько. */
export interface МестоВМесяце {
  readonly сосед: MonthSiblingOut;
  readonly название: string;
  readonly сумма: number;
}

/**
 * Что делается с колонкой — подлежащее и глагол, согласованные между собой.
 *
 * Заголовок колонки для этого не годится: он слово заказчика («Билеты», «Квартиры»), и фраза
 * «билеты удерживается» читается как ошибка программы. Колонки, которых здесь нет, называются
 * своим заголовком в кавычках, через «удержание … стоит»: так фраза верна при любом слове.
 */
const ЧТО_С_КОЛОНКОЙ: Readonly<Record<string, string>> = {
  [COLUMN_ADVANCE]: 'аванс удерживается',
  uniform: 'форма удерживается',
  tickets: 'билет удерживается',
  training: 'обучение удерживается',
  license: 'удостоверение удерживается',
  credit: 'кредит удерживается',
  housing: 'квартиры удерживаются',
  [COLUMN_OFFICIAL_PAID]: 'уже выплаченная официальная часть вычитается',
  [COLUMN_OFFICIAL_REST]: 'остаток официальной части вычитается',
};

/**
 * Колонки, по которым месяц раскладывается между ведомостями: статьи месяца — долги по графику
 * и разом, аванс, официальная часть. Начисления и итог — свои у каждой ведомости.
 *
 * Колонки, вводимые руками (штраф, «зарплата на объектах», алименты), — не статьи месяца: их
 * ввели в своей ведомости, и подпись «здесь не удерживается, чтобы не взять дважды» про них
 * неправда (ревью 20.10.2026). Долги процентом от выплаты берутся в каждой ведомости своей
 * долей — их сервер в суммах соседа не присылает вовсе.
 */
function раскладывается(колонка: PayrollColumnOut): boolean {
  if (колонка.behaviour === 'input') {
    return false;
  }
  return колонка.kind === 'deduction' || колонка.kind === 'official_split';
}

/** Сумма колонки у соседа. `null` — сумма скрыта ролью, о ней молчим. */
function суммаСоседа(сосед: MonthSiblingOut, код: string): number | null {
  switch (код) {
    case COLUMN_ADVANCE:
      // Как и в своей ячейке: аванс месяца вместе с неудержанным авансом прошлых месяцев.
      return авансВКолонке(сосед);
    case COLUMN_OFFICIAL_PAID:
      return сосед.official_paid;
    case COLUMN_OFFICIAL_REST:
      return сосед.official_rest;
    default:
      return сосед.amounts[код] ?? 0;
  }
}

/** Видимые соседи, у которых по этой колонке стоят деньги. */
export function местаВМесяце(человек: PayrollEmployeeOut, код: string): МестоВМесяце[] {
  return (человек.month_siblings ?? [])
    .map((сосед) => ({
      сосед,
      название: сосед.site_name ?? АППАРАТ,
      сумма: суммаСоседа(сосед, код) ?? 0,
    }))
    .filter((место) => место.сумма > 0);
}

/** «в ведомости «Бур-Сервис»: 1 000 ₽» либо «в ведомостях «А»: 500 ₽ и «Б»: 500 ₽». */
function гдеСловами(места: readonly МестоВМесяце[]): string {
  const части = места.map((место) => `«${место.название}»: ${formatRubles(место.сумма)}`);
  if (части.length === 1) {
    return `в ведомости ${части[0]}`;
  }
  return `в ведомостях ${части.slice(0, -1).join(', ')} и ${части[части.length - 1]}`;
}

/**
 * Подсказка ячейки о других ведомостях месяца. `null` — сказать нечего: соседей с деньгами по
 * этой колонке нет либо смотрящему они не видны.
 *
 * Ноль в ячейке — «За сентябрь 2026 форма удерживается в ведомости «Бур-Сервис»: 1 000 ₽. Здесь
 * не удерживается, чтобы не взять дважды.» Сумма в ячейке, а часть месяца в соседней —
 * «Остальное за сентябрь 2026 удерживается в ведомости «Н. Уренгой»: 2 056 ₽.»
 */
export function подсказкаМесяца(
  колонка: PayrollColumnOut,
  человек: PayrollEmployeeOut,
  значение: number,
  периодСловами: string | null,
): string | null {
  if (!раскладывается(колонка)) {
    return null;
  }
  const места = местаВМесяце(человек, колонка.code);
  if (места.length === 0) {
    return null;
  }
  const где = гдеСловами(места);
  const за = периодСловами === null ? 'этот месяц' : периодСловами;
  const официальная = колонка.kind === 'official_split';
  if (значение === 0) {
    const что = ЧТО_С_КОЛОНКОЙ[колонка.code] ?? `удержание «${колонка.title}» стоит`;
    const почему = официальная
      ? 'Здесь не вычитается, чтобы не вычесть дважды.'
      : 'Здесь не удерживается, чтобы не взять дважды.';
    return `За ${за} ${что} ${где}. ${почему}`;
  }
  return `Остальное за ${за} ${официальная ? 'вычитается' : 'удерживается'} ${где}.`;
}
